import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import NotificationsPortal from '../NotificationsPortal';

const MAX_VISIBLE_COLLAPSED = 3;

const ToastStack = ({ items, renderToast }) => {
  const [touchExpanded, setTouchExpanded] = useState(false);
  const itemRefs = useRef(new Map());
  const [heights, setHeights] = useState({});

  useEffect(() => {
    setTouchExpanded(false);
  }, [items.length]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      setHeights(prev => {
        let changed = false;
        const next = { ...prev };

        entries.forEach(entry => {
          const id = entry.target.dataset.toastId;
          const height = entry.contentRect.height;

          if (id && next[id] !== height) {
            next[id] = height;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    });

    itemRefs.current.forEach(node => {
      if (node) {
        observer.observe(node);
      }
    });

    return () => observer.disconnect();
  }, [items.length]);

  const setRef = useCallback(
    id => node => {
      if (node) {
        itemRefs.current.set(id, node);
      } else {
        itemRefs.current.delete(id);
      }
    },
    [],
  );

  if (items.length === 0) {
    return null;
  }

  let cumulativeLift = 0;

  return (
    <NotificationsPortal className="toast-stack-portal">
      <div className={classnames('toast-stack', { 'toast-stack--expanded': touchExpanded })}>
        {items.map((toast, index) => {
          const liftPx = cumulativeLift;
          const itemHeight = heights[toast.id] || 0;
          const gap = 8;
          cumulativeLift += itemHeight + gap;

          const hidden = index >= MAX_VISIBLE_COLLAPSED;

          return (
            <div
              className={classnames('toast-stack__item', {
                'toast-stack__item--hidden': hidden,
              })}
              data-index={index}
              data-toast-id={toast.id}
              key={toast.id}
              ref={setRef(toast.id)}
              style={{
                '--toast-index': index,
                '--toast-lift': `${liftPx}px`,
              }}
            >
              {renderToast(toast)}
            </div>
          );
        })}
      </div>
    </NotificationsPortal>
  );
};

ToastStack.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
  ).isRequired,
  renderToast: PropTypes.func.isRequired,
};

export default ToastStack;
